import React, { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import useBundleTranslation from 'i18n';

import { useHomeContext } from 'app/home/context';
import { useQuery } from '@tanstack/react-query';

import { HiddenItemsCountResponse, HiddenItemsElement, HiddenItemsResponse, homePage } from 'api/home-page';
import { debounce } from 'lodash';
import md5 from 'crypto-js/md5';
import { alpha, Box, FormLabel, InputAdornment, Link, Stack, TextField, Tooltip } from '@mui/material';
import { Popup } from 'components/common/popup/Popup';
import IconMi from 'components/common/icon/IconMi';
import { getEditUrl, getViewUrl, removeHtmlTags } from 'tools/tools';
import { useNavigate } from 'react-router-dom';
import { useIsAdmin } from 'hooks/useUserPrivilege';
import { useDebounce } from 'use-debounce';
import { HtmlTooltip } from 'components/html-tooltip';
import { TileFavoriteIcon, TileFavoriteIconInfo } from 'app/home/tile/components/TileFavoriteIcon';
import { AccessDeniedTileInfo } from 'app/home/index.functions';
import CertificationData from 'components/common/CertificationData';

export const HiddenItemInfoIcon = function ({ element }: { element: HiddenItemsElement }) {
    const { t } = useBundleTranslation(['app/home']);
    const [mouseOverInfoIcon, setMouseOverInfoIcon] = useState(false);
    const [mouseOverInfoPanel, setMouseOverInfoPanel] = useState(false);

    const [debouncedShowPanel] = useDebounce(mouseOverInfoIcon, 100);

    return (
        <HtmlTooltip
            placement={'right-start'}
            arrow={false}
            certified={element.certified_ind === 'Y'}
            certifiedColor={element.certification_level_color || ''}
            title={
                <Stack
                    sx={{ p: 1 }}
                    onMouseLeave={() => {
                        setMouseOverInfoIcon(false);
                        setMouseOverInfoPanel(false);
                    }}
                    onMouseEnter={() => {
                        setMouseOverInfoIcon(false);
                        setMouseOverInfoPanel(true);
                    }}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                >
                    <Box>{element.last_measurement_time_formatted}</Box>
                    {element.last_measurement_value_formatted && (
                        <Box>{`${t('value')} ${element.last_measurement_value_formatted}`}</Box>
                    )}
                    {removeHtmlTags(element.element_info)}
                    {element.certified_ind === 'Y' && (
                        <Box sx={{ display: 'flex' }}>
                            <IconMi
                                icon="certified"
                                sx={{
                                    fontSize: '16px',
                                    color: element.certification_level_color || 'success.main',
                                    mr: 0.5,
                                }}
                            />
                            <Box>
                                <CertificationData
                                    certificationData={{
                                        color: element.certification_level_color as string | undefined,
                                        name: element.certification_level_name as string | undefined,
                                        lastUpdatedBy: element.last_certified_by_name as string | undefined,
                                        lastUpdatedTime: element.last_certified_time as string | undefined,
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                </Stack>
            }
            open={removeHtmlTags(element.element_info) > '' && (debouncedShowPanel || mouseOverInfoPanel)}
        >
            <Box
                onMouseLeave={() => {
                    setMouseOverInfoIcon(false);
                }}
                onMouseEnter={(event) => {
                    setMouseOverInfoIcon(true);
                }}
            >
                {element.certified_ind === 'Y' ? (
                    <Box
                        sx={{
                            display: 'flex',
                            color: element.certification_level_color || 'success.main',
                        }}
                    >
                        <IconMi icon={'certified'} sx={{ fontSize: '16px' }} />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            color: (theme) => alpha(theme.palette.text.primary, 0.64),
                        }}
                    >
                        <IconMi icon={'info-circle'} sx={{ fontSize: '16px' }} />
                    </Box>
                )}
            </Box>
        </HtmlTooltip>
    );
};

function HiddenItemsPopup({
    openPopup,
    setOpenPopup,
}: {
    openPopup: boolean;
    setOpenPopup: (openPopup: boolean) => void;
}) {
    const { t } = useBundleTranslation(['app/home']);
    const [search, setSearch] = useState('');
    const [initState, setInitState] = useState(true);
    const navigate = useNavigate();
    const isAdmin = useIsAdmin();

    const ctx = useHomeContext();
    const [homePageState, favorites, setTile4Favorite, setAccessDeniedPopupTile, segmentOptions, tagOptions] = [
        ctx.homePageState,
        ctx.favorites,
        ctx.setTile4Favorite,
        ctx.setAccessDeniedPopupTile,
        ctx.segmentOptions,
        ctx.tagOptions,
    ];
    let queryKey = ['hiddenDataElements_' + md5(JSON.stringify(homePageState.filterState))];
    const {
        data: elementsData,
        isLoading,
        refetch,
    } = useQuery<HiddenItemsResponse>({
        queryKey: queryKey,
        queryFn: () => {
            if (
                homePageState.filterState.keywordFilter > '' ||
                homePageState.filterState.segmentFilter > '' ||
                homePageState.filterState.tagFilter.length > 0
            )
                return homePage.loadHiddenItems(homePageState.filterState);

            return { elements: [] };
        },
    });
    const handleInputUpdate = (event: ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
        setInitState(false);
    };

    const handleInputClear = () => {
        setSearch('');
    };

    const filterText = (element: HiddenItemsElement) => {
        let tmp = search.split(' ');

        for (let i = 0; i < tmp.length; i++)
            if (element.element_dashboard_name.toLowerCase().indexOf(tmp[i].toLowerCase()) < 0) return false;

        return true;
    };

    if (isLoading) return null;
    const listPreFiltered =
        homePageState.filterState.segmentFilter > '' || homePageState.filterState.tagFilter.length > 0;

    let listPreFilteredText: string[] = [];
    if (listPreFiltered) {
        if (homePageState.filterState.segmentFilter > '') {
            let f = segmentOptions.find((a) => a.value == homePageState.filterState.segmentFilter);

            if (undefined !== f) listPreFilteredText.push(f.label);
        }

        if (homePageState.filterState.tagFilter.length > 0) {
            let f = tagOptions.filter((a) => homePageState.filterState.tagFilter.includes(a.value));

            if (undefined !== f && f.length) for (const fKey in f) listPreFilteredText.push(f[fKey].label);
        }
    }

    return (
        <Popup
            settings={{
                title: t('hidden_items.title'),
                needTranslation: false,
                noButtons: true,
            }}
            maxWidth="popupLg"
            open={openPopup}
            onHide={() => setOpenPopup(false)}
            onConfirm={() => setOpenPopup(false)}
        >
            <Stack direction={'column'}>
                {homePageState.filterState.keywordFilter > '' && (
                    <FormLabel>
                        {t('hidden_items.for_your_search') + ' '}
                        <Box component={'span'} sx={{ color: 'primary.main', fontWeight: 600 }}>
                            {homePageState.filterState.keywordFilter}
                        </Box>
                    </FormLabel>
                )}

                <Box key={`hidden-items-filter`}>
                    <TextField
                        placeholder={t('search')}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ color: 'primary.main' }}>
                                    <IconMi icon="search" fontSize="16" />
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment
                                    onMouseDown={handleInputClear}
                                    position="end"
                                    sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { opacity: 0.75 } }}
                                >
                                    <IconMi icon="times" fontSize="16" />
                                </InputAdornment>
                            ) : undefined,
                        }}
                        fullWidth
                        value={search}
                        onChange={handleInputUpdate}
                        data-test={`hidden-items-filter`}
                    />
                </Box>
                {listPreFiltered && (
                    <FormLabel sx={{ mt: 1.5, fontWeight: 600 }}>
                        {t('hidden_items.list_pre_filtered_by') + ': '}
                        <Box component={'span'} sx={{ color: 'primary.main' }}>
                            {listPreFilteredText.join(', ')}
                        </Box>
                    </FormLabel>
                )}

                {elementsData && (
                    <Stack sx={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: '500px' }}>
                        <Stack sx={{ mt: 1 }}>
                            {elementsData.elements
                                .filter(filterText)
                                .slice(0, 2000)
                                .map((element, i) => {
                                    return (
                                        <Stack
                                            direction={'row'}
                                            alignItems={'center'}
                                            key={`hidden-item-box-${element.user_dashboard_element_instance_id}`}
                                            sx={{
                                                borderBottom: '1px solid',
                                                borderColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                                py: 0.75,
                                            }}
                                        >
                                            <Box
                                                key={`hidden-item-${element.user_dashboard_element_instance_id}`}
                                                sx={{ flexGrow: 1, overflow: 'hidden' }}
                                            >
                                                <Box
                                                    sx={{
                                                        textOverflow: 'ellipsis',
                                                        overflow: 'hidden',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {'Y' == element.has_access ? (
                                                        <Box
                                                            component={'a'}
                                                            href={
                                                                '' +
                                                                getViewUrl(element.element_type || '') +
                                                                element.element_id
                                                            }
                                                            target={'_blank'}
                                                            sx={{ color: 'text.primary' }}
                                                        >
                                                            {element.element_dashboard_name}
                                                        </Box>
                                                    ) : (
                                                        <Box
                                                            onClick={(e) => {
                                                                setAccessDeniedPopupTile(
                                                                    element as AccessDeniedTileInfo,
                                                                );
                                                            }}
                                                            sx={{
                                                                color: 'text.primary',
                                                                '&:hover': {
                                                                    textDecoration: 'underline',
                                                                },
                                                            }}
                                                        >
                                                            {element.element_dashboard_name}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                            {'Y' == element.has_access && (
                                                <Stack
                                                    direction={'row'}
                                                    spacing={1}
                                                    key={`hidden-item-${element.user_dashboard_element_instance_id}`}
                                                    sx={{
                                                        flexShrink: 0,
                                                        alignItems: 'center',
                                                        width: '65px',
                                                        ml: 1,
                                                    }}
                                                >
                                                    <TileFavoriteIcon
                                                        tile={element as TileFavoriteIconInfo}
                                                        setTile4Favorite={setTile4Favorite}
                                                        favorites={favorites}
                                                    />
                                                    {(isAdmin || 'Y' == element.is_edit_access_ind) && (
                                                        <Tooltip
                                                            title={
                                                                t('edit') +
                                                                    ' ' +
                                                                    (element.element_type as string).replace(
                                                                        'internal',
                                                                        '',
                                                                    ) || ''
                                                            }
                                                        >
                                                            <Link
                                                                href={
                                                                    '' +
                                                                    getEditUrl(element.element_type || '') +
                                                                    element.element_id
                                                                }
                                                                target={'_blank'}
                                                                sx={{ display: 'inline-flex' }}
                                                            >
                                                                <IconMi icon={'gear'} fontSize={'15'} />
                                                            </Link>
                                                        </Tooltip>
                                                    )}
                                                    <HiddenItemInfoIcon element={element} />
                                                </Stack>
                                            )}
                                        </Stack>
                                    );
                                })}
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </Popup>
    );
}

export default function HiddenItems({}: {}) {
    const [hiddenItemsCountData, setHiddenItemsCountData] = useState<HiddenItemsCountResponse | null>(null);
    const [openPopup, setOpenPopup] = useState<boolean>(false);

    const ctx = useHomeContext();

    const [homePageState, setHomePageState, settings] = [ctx.homePageState, ctx.setHomePageState, ctx.homePageSettings];
    const { t } = useBundleTranslation(['app/home']);

    const debouncedChangeHandler = useMemo(
        () =>
            debounce(() => {
                if (
                    homePageState.filterState.keywordFilter > '' ||
                    homePageState.filterState.segmentFilter > '' ||
                    homePageState.filterState.tagFilter.length > 0
                )
                    homePage.loadHiddenItemsCount(homePageState.filterState).then((tmp) => {
                        if (undefined != tmp) setHiddenItemsCountData(tmp);
                    });
                else setHiddenItemsCountData({ count: 0 });
            }, 500),
        [homePageState.filterState],
    );

    useEffect(() => {
        debouncedChangeHandler();

        return () => {
            debouncedChangeHandler.cancel();
        };
    }, [
        homePageState.filterState.keywordFilter,
        homePageState.filterState.segmentFilter,
        homePageState.filterState.tagFilter,
    ]);

    if (undefined == hiddenItemsCountData) return null;

    let condFilter = homePageState.filterState.keywordFilter > '';

    return (
        <>
            {hiddenItemsCountData?.count > 0 && (
                <Box
                    sx={{
                        mx: 3,
                        mt: 1,
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                        padding: '6px 12px',
                        borderRadius: '4px',
                        lineHeight: '16px',
                    }}
                >
                    <IconMi
                        icon={'hide'}
                        sx={{
                            color: 'primary.main',
                            fontSize: '16px',
                            mr: 1,
                            display: 'inline-flex',
                            verticalAlign: 'top',
                        }}
                    />
                    <Box component={'span'} sx={{ color: 'primary.main' }}>
                        {t('hidden_items.item', { count: hiddenItemsCountData?.count })}
                    </Box>{' '}
                    {t('hidden_items.match_your_search')}{' '}
                    {condFilter ? (
                        <>
                            {t('hidden_items.for')}{' '}
                            <Box component={'span'} sx={{ fontWeight: '600' }}>
                                {homePageState.filterState.keywordFilter}
                            </Box>
                        </>
                    ) : (
                        <Box component={'span'} sx={{ fontWeight: '600' }}>
                            {t('hidden_items.criteria')}
                        </Box>
                    )}{' '}
                    {t('hidden_items.but_have_been_marked_to_not_be_visible_on_the_homepage')}
                    {'. '}
                    <Box className={'invert-link'} component={'span'} onClick={() => setOpenPopup(true)}>
                        {t('hidden_items.show_them')}
                    </Box>
                </Box>
            )}

            {hiddenItemsCountData?.count > 0 && openPopup && (
                <HiddenItemsPopup openPopup={openPopup} setOpenPopup={setOpenPopup} />
            )}
        </>
    );
}
